import {
    ThemeEvents,
    VariantSelectedEvent,
    VariantUpdateEvent,
    RugPadToggleEvent,
} from "@theme/events";
import { shopifyFormatMoney } from "@theme/utilities";

export default class SimpleRugPadPicker extends HTMLElement {
    #abortController = new AbortController();

    connectedCallback() {
        const { signal } = this.#abortController;
        const target = this.closest(".product-info");
        this.initLazySection();
        this.selectedRugPadId = null;

        this.addEventListener("change", this.onChangeRugPad.bind(this));
        this.addEventListener("click", this.clickHandler.bind(this));

        target?.addEventListener(
            ThemeEvents.variantUpdate,
            this.#onVariantUpdate.bind(this),
            { signal },
        );
    }

    disconnectedCallback() {
        this.#abortController.abort();
    }

    initLazySection() {
        try {
            if (this.selectedVariantSku) {
                const variant = JSON.parse(
                    document.querySelector(
                        'new-variant-picker script[type="application/json"]',
                    ).textContent,
                );

                if (!variant) return;

                const { shape, width_in, length_in } = variant?.metafields;
                this.findSuitableRugPad(
                    shape,
                    parseInt(length_in, 10),
                    parseInt(width_in, 10),
                );
            }
        } catch (error) {
            console.error(error);
        }
    }

    onChangeRugPad(event) {
        const { target } = event;
        if (target instanceof HTMLInputElement) {
            const templateInput = this.querySelector(
                `label input[value="${target.value}"]`,
            );

            if (target.checked) {
                const templateInputs = this.querySelectorAll("input");
                const inputs = this.querySelectorAll("input");
                templateInput.checked = true;
                this.form.querySelector('input[name="id"]').value =
                    target.value;
                inputs.forEach((input) => {
                    if (input !== target) input.checked = false;
                });

                templateInputs.forEach((input) => {
                    if (input.value !== target.value) input.checked = false;
                });
                this.selectedRugPadId = target.value;
            } else {
                templateInput.checked = false;
                this.selectedRugPadId = null;
            }

            this.dispatchEvent(
                new RugPadToggleEvent(target.checked ? this.form : null),
            );
        }
    }

    clickHandler(event) {
        const { target } = event;
        if (target instanceof HTMLInputElement) {
            if (target.checked) {
                if (this.selectedRugPadId === target.value) {
                    target.checked = false;
                    this.selectedRugPadId = null;
                    this.dispatchEvent(new RugPadToggleEvent(null));
                }
            }
        }
    }

    renderLabels() {
        try {
            if (this.labels.length > 0) {
                let checkedInput = null;
                this.labels.forEach((item) => {
                    const input = item?.querySelector("input");
                    const isAvailable = item?.dataset.available === "true";
                    input.setAttribute("data-option-available", isAvailable);
                    if (isAvailable) {
                        input.removeAttribute("disabled");
                    } else {
                        input.setAttribute("disabled", "");
                        if (input.checked) {
                            input.checked = false;
                            input.blur();
                        }
                    }
                    const priceText = item?.querySelector("[data-price-span]");
                    if (priceText) {
                        if (isAvailable) {
                            priceText.removeAttribute("hidden");
                        } else {
                            priceText.setAttribute("hidden", "");
                        }
                    }
                    item.style.cursor = isAvailable ? "pointer" : "default";

                    if (input.checked && isAvailable) checkedInput = input;
                });

                if (!checkedInput) {
                    this.dispatchEvent(new RugPadToggleEvent(null));
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * @param {VariantSelectedEvent} event
     */
    #onVariantSelect(event) {
        console.log(event);
    }

    /**
     * @param {VariantUpdateEvent} event
     */
    #onVariantUpdate(event) {
        if (!event?.detail?.sourceId) return;
        const variant =
            event?.detail?.data?.variant ||
            Object.assign(
                { ...event.detail.resource.variant },
                { metafields: { ...event.detail.resource.metafields } },
            );

        this.findSuitableRugPad(
            variant.metafields.shape,
            parseInt(variant.metafields?.length_in),
            parseInt(variant.metafields?.width_in),
        );
    }

    findSuitableRugPad(shape, length, width) {
        if (length <= 0 || width <= 0) return;
        let values = null;

        if (shape === "Area Rug" || shape === "Small") {
            if (length <= 40 && width <= 28) {
                values = this.getRugPadVariantData([
                    "LXG-24",
                    "LXG-35",
                    "LXG-46",
                    "LXG-5373",
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-23",
                    "PADF-24",
                    "PADF-35",
                    "PADF-46",
                    "PADF-5610",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 52 && width <= 28) {
                values = this.getRugPadVariantData([
                    "LXG-24",
                    "LXG-35",
                    "LXG-46",
                    "LXG-5373",
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-24",
                    "PADF-35",
                    "PADF-46",
                    "PADF-5610",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 64 && width <= 40) {
                values = this.getRugPadVariantData([
                    "LXG-35",
                    "LXG-46",
                    "LXG-5373",
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-35",
                    "PADF-46",
                    "PADF-5610",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 76 && width <= 52) {
                values = this.getRugPadVariantData([
                    "LXG-46",
                    "LXG-5373",
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-46",
                    "PADF-5610",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 87 && width <= 63) {
                values = this.getRugPadVariantData([
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-5610",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 92 && width <= 67) {
                values = this.getRugPadVariantData([
                    "LXG-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-58",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-912",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                    "PADF-1318",
                ]);
            } else if (length <= 100 && width <= 64) {
                values = this.getRugPadVariantData([
                    "LXG-58",
                    "PADF-58",
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 112 && width <= 76) {
                values = this.getRugPadVariantData([
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-69",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 114 && width <= 79) {
                // "LXG-69","LXG-6796","LXG-810","LXG-811","LXG-912","LXG-913","LXG-1014","LXG-1215" 2026/01/08 added
                values = this.getRugPadVariantData([
                    "LXG-69",
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-65810",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 118 && width <= 84) {
                values = this.getRugPadVariantData([
                    "LXG-6796",
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-69",
                ]);
            } else if (length <= 124 && width <= 100) {
                values = this.getRugPadVariantData([
                    "LXG-810",
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-7898",
                    "PADF-810",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 136 && width <= 100) {
                values = this.getRugPadVariantData([
                    "LXG-811",
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-811",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 148 && width <= 116) {
                values = this.getRugPadVariantData([
                    "LXG-912",
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-912",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                    "PADF-1318",
                ]);
            } else if (length <= 152 && width <= 112) {
                values = this.getRugPadVariantData([
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-881110",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 160 && width <= 112) {
                values = this.getRugPadVariantData([
                    "LXG-913",
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-913",
                ]);
            } else if (length <= 160 && width <= 116) {
                values = this.getRugPadVariantData([
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-913",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 172 && width <= 124) {
                values = this.getRugPadVariantData([
                    "LXG-1014",
                    "LXG-1215",
                    "PADF-1014",
                    "PADF-1215",
                ]);
            } else if (length <= 184 && width <= 148) {
                values = this.getRugPadVariantData(["LXG-1215", "PADF-1215"]);
            } else if (length <= 222 && width <= 162) {
                values = this.getRugPadVariantData([false, "PADF-1318"]);
            }
        } else if (shape == "Runner") {
            if (length <= 100 && width <= 28) {
                values = this.getRugPadVariantData([
                    "LXG-28",
                    "LXG-2773",
                    "LXG-2610",
                    "LXG-312",
                    "PADF-28",
                    "PADF-257",
                    "PADF-2578",
                    "PADF-2599",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 87 && width <= 31) {
                values = this.getRugPadVariantData([
                    "LXG-2773",
                    "LXG-2773",
                    "LXG-2610",
                    "LXG-312",
                    "PADF-257",
                    "PADF-2578",
                    "PADF-2599",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 92 && width <= 34) {
                values = this.getRugPadVariantData([
                    "LXG-2773",
                    "LXG-2610",
                    "LXG-312",
                    "PADF-28",
                    "PADF-2578",
                    "PADF-2599",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 96 && width <= 31) {
                values = this.getRugPadVariantData([
                    "LXG-2610",
                    "LXG-312",
                    "PADF-2578",
                    "PADF-2599",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 124 && width <= 31) {
                values = this.getRugPadVariantData([
                    "LXG-2610",
                    "LXG-312",
                    "PADF-2599",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 148 && width <= 31) {
                values = this.getRugPadVariantData([
                    "LXG-312",
                    "PADF-25119",
                    "PADF-2610",
                    "PADF-312",
                    "PADF-410",
                ]);
            } else if (length <= 124 && width <= 34) {
                values = this.getRugPadVariantData(["LXG-2610", "PADF-2610"]);
            } else if (length <= 124 && width <= 52) {
                values = this.getRugPadVariantData([false, "PADF-410"]);
            } else if (length <= 148 && width <= 40) {
                values = this.getRugPadVariantData(["LXG-312", "PADF-312"]);
            }
        } else if (shape == "Round") {
            if (length <= 40 && width <= 40) {
                values = this.getRugPadVariantData([
                    "LXG-3RD",
                    "LXG-4RD",
                    "LXG-6RD",
                    "LXG-8RD",
                    "LXG-99RD",
                    "LXG-99RD",
                    "PADF-3RD",
                    "PADF-4RD",
                    "PADF-6RD",
                    "PADF-8RD",
                    "PADF-99RD",
                    "PADF-10RD",
                ]);
            } else if (length <= 52 && width <= 52) {
                values = this.getRugPadVariantData([
                    "LXG-4RD",
                    "LXG-6RD",
                    "LXG-8RD",
                    "LXG-99RD",
                    "LXG-99RD",
                    "PADF-4RD",
                    "PADF-6RD",
                    "PADF-8RD",
                    "PADF-99RD",
                    "PADF-10RD",
                ]);
            } else if (length <= 76 && width <= 76) {
                values = this.getRugPadVariantData([
                    "LXG-6RD",
                    "LXG-8RD",
                    "LXG-99RD",
                    "LXG-99RD",
                    "PADF-6RD",
                    "PADF-8RD",
                    "PADF-99RD",
                    "PADF-10RD",
                ]);
            } else if (length <= 100 && width <= 100) {
                values = this.getRugPadVariantData([
                    "LXG-8RD",
                    "LXG-99RD",
                    "LXG-99RD",
                    "PADF-8RD",
                    "PADF-99RD",
                    "PADF-10RD",
                ]);
            } else if (length <= 121 && width <= 121) {
                values = this.getRugPadVariantData([
                    "LXG-8RD",
                    "LXG-99RD",
                    "PADF-8RD",
                    "PADF-10RD",
                ]);
            } else if (length <= 124 && width <= 124) {
                values = this.getRugPadVariantData(["LXG-99RD", "PADF-10RD"]);
            }
        } else if (shape == "Square") {
            if (length <= 52 && width <= 52) {
                values = this.getRugPadVariantData([
                    "LXG-46",
                    "LXG-58",
                    "LXG-8SQ",
                    "LXG-8SQ",
                    "LXG-1014",
                    "LXG-1014",
                    "PADF-46",
                    "PADF-58",
                    "PADF-8SQ",
                    "PADF-8SQ",
                    "PADF-99SQ",
                    "PADF-99SQ",
                ]);
            } else if (length <= 64 && width <= 64) {
                values = this.getRugPadVariantData([
                    "LXG-58",
                    "LXG-8SQ",
                    "LXG-8SQ",
                    "LXG-1014",
                    "PADF-58",
                    "PADF-8SQ",
                    "PADF-8SQ",
                    "PADF-99SQ",
                ]);
            } else if (length <= 83 && width <= 83) {
                values = this.getRugPadVariantData([
                    "LXG-8SQ",
                    "LXG-1014",
                    "PADF-8SQ",
                    "PADF-8SQ",
                    "PADF-99SQ",
                ]);
            } else if (length <= 100 && width <= 100) {
                values = this.getRugPadVariantData([
                    "LXG-8SQ",
                    "LXG-1014",
                    "PADF-8SQ",
                    "PADF-99SQ",
                ]);
            } else if (length <= 121 && width <= 121) {
                values = this.getRugPadVariantData(["LXG-1014", "PADF-99SQ"]);
            } else if (length >= 121 && width >= 121) {
                values = this.getRugPadVariantData(["LXG-1014", "PADF-99SQ"]);
            }
        } else if (shape == "Oval") {
            if (length <= 64 && width <= 40) {
                values = this.getRugPadVariantData([
                    "LXG-35",
                    "LXG-46",
                    "LXG-58",
                    "LXG-69OV",
                    "LXG-810OV",
                    "PADF-35",
                    "PADF-46",
                    "PADF-58",
                    "PADF-69OV",
                    "PADF-810OV",
                ]);
            } else if (length <= 76 && width <= 52) {
                values = this.getRugPadVariantData([
                    "LXG-46",
                    "LXG-58",
                    "LXG-69OV",
                    "LXG-810OV",
                    "PADF-46",
                    "PADF-58",
                    "PADF-69OV",
                    "PADF-810OV",
                ]);
            } else if (length <= 100 && width <= 64) {
                values = this.getRugPadVariantData([
                    "LXG-58",
                    "LXG-69OV",
                    "LXG-810OV",
                    "PADF-58",
                    "PADF-69OV",
                    "PADF-810OV",
                ]);
            } else if (length <= 112 && width <= 76) {
                values = this.getRugPadVariantData([
                    "LXG-69OV",
                    "LXG-810OV",
                    "PADF-69OV",
                    "PADF-810OV",
                ]);
            } else if (length <= 124 && width <= 100) {
                values = this.getRugPadVariantData(["LXG-810OV", "PADF-810OV"]);
            }
        }

        for (const label of this.labels) {
            const id = label.dataset.id;
            const findedItem = values?.find((e) => e.id === id);
            const settings = {};

            if (id === this.excludeRugPadId) {
                const data = JSON.parse(
                    label.querySelector('script[type="application/json"]')
                        .textContent,
                );
                const variant = data?.variants[0] ?? null;
                settings.available = variant.available;
                settings.id = variant.id;
                settings.price = variant.price;
            } else if (findedItem?.variant) {
                settings.available = findedItem?.variant?.available;
                settings.id = findedItem.variant.id;
                settings.price = findedItem.variant.price;
            } else {
                settings.available = false;
                settings.id = null;
                settings.price = 0;
                settings.checked = false;
            }
            this.setElementSettings(label, settings);
        }

        this.renderLabels();
    }

    getRugPadVariantData(skus = []) {
        const data = [];
        this.rugPadVariants.forEach((item) => {
            let findedVariant = null;
            const findedVariants = item.variants.filter((e) =>
                skus.includes(e.sku),
            );
            if (findedVariants?.length === 1) {
                findedVariant = findedVariants[0];
            } else if (findedVariants?.length > 1) {
                findedVariant = findedVariants.find(
                    (e) => e.available === true,
                );
            }

            if (findedVariant) {
                data.push({
                    id: item.id,
                    variant: findedVariant,
                });
            }
        });

        return data;
    }

    setElementSettings(element, settings) {
        const input = element?.querySelector("input");
        const priceSpan = element.querySelector("[data-price-span]");
        element.dataset.available = settings.available;
        input.value = settings.id;
        input.dataset.available = settings.available;
        if (settings.hasOwnProperty("checked"))
            input.checked = settings.checked;
        if (priceSpan) priceSpan.innerHTML = shopifyFormatMoney(settings.price);
    }

    get labels() {
        return this.querySelectorAll("label");
    }

    get rugPadVariants() {
        if (this.labels.length <= 0) return [];
        const rugPadVariantList = [];
        this.labels.forEach((label) => {
            const data =
                JSON.parse(label.querySelector("script").innerText) ?? {};
            rugPadVariantList.push(data);
        });

        return rugPadVariantList;
    }

    get selectedVariantSku() {
        return this.dataset.selectedVariant;
    }

    get excludeRugPadId() {
        return "7456571621568";
    }

    get form() {
        return this.querySelector("form");
    }
    get wrapper() {
        return this.querySelector(".option-selector__btns");
    }
}

if (!customElements.get("simple-rug-pad-picker")) {
    customElements.define("simple-rug-pad-picker", SimpleRugPadPicker);
}
